/**
 * Cette fonction prend un objet srcset et une taille voulue, et renvoie la taille disponible la plus proche.
 * @param {Array} srcset - L'objet srcset retourné par la fonction getSrcset.
 * @param {number | object} wantedSize - La taille que vous voulez obtenir.
 * @param {string} [format=null] - Le format optionnel que vous voulez obtenir.
 * @returns {object} - La taille disponible la plus proche de la taille voulue.
 */
export const getClosestAvailableSize = (srcset, wantedSize, format = null) => {
  const filteredSrcset = format ? srcset.filter(set => set.format === format) : srcset;

  return filteredSrcset.reduce((closest, current) => {
    const isWantedSizeObject = typeof wantedSize === 'object';
    const isCurrentLargerOrEqual = isWantedSizeObject ? current.width >= wantedSize.width && current.height >= wantedSize.height : current.width >= wantedSize;

    if (isCurrentLargerOrEqual) {
      const isClosestLargerOrEqual = isWantedSizeObject ? closest && closest.width >= wantedSize.width && closest.height >= wantedSize.height : closest && closest.width >= wantedSize;

      if (isClosestLargerOrEqual) {
        return (current.width < closest.width && current.height < closest.height) ? current : closest;
      } else {
        return current;
      }
    } else {
      const isClosestSmaller = isWantedSizeObject ? closest && closest.width >= current.width && closest.height >= current.height : closest && closest.width >= current.width;
      return isClosestSmaller ? closest : current;
    }
  }, null);
}

/**
 * Filters the srcset to return the source with the specified size.
 * @param {Array} srcset - The array of source objects.
 * @param {number} size - The desired width of the source.
 * @returns {Array} - An array of sources that match the specified size.
 */
export function getSrc(srcset, size) {
  return srcset.src.filter((src) => src.width === size);
}


/**
 * This function imports a JSON file and returns an array of objects containing the details of the closest available sizes and their corresponding content.
 * @param {string} fileName - The name of the JSON file to import (without the .json extension).
 * @param {number | object} [wantedSize=1440] - The desired size of the image. Can be a number or an object with width and height properties.
 * @param {string} [format=null] - The optional format of the image.
 * @returns {Promise<Array>} - A promise that resolves to an array of objects containing the details of the closest available sizes and their corresponding content.
 */
export const importer = async (fileName, wantedSize = 1440, format = null) => {
  // Import the JSON file corresponding to the provided file name.
  const json = await import(`./${fileName}.json`);

  // Extract a unique list of formats available in the JSON file.
  const formatList = json.src
    .map((src) => src.format)
    .filter((format, index, self) => self.indexOf(format) === index);

  // Determine the srcset to use based on the presence of a specific requested format.
  const srcset = format ? [getClosestAvailableSize(json.src, wantedSize, format)] : formatList.map((f) => getClosestAvailableSize(json.src, wantedSize, f));

  // For each element in the srcset, import the corresponding file and return an object containing the element details and file content.
  return await Promise.all(srcset.map(async (set) => {
    const srcObj = await import(`./${set.path}`);
    const src = srcObj.default;
    return { ...set, src };
  }));
}
